import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { SortableTableHead } from '@mdvip/shared/components/blocks/SortableTableHead';
import {
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableError,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@mdvip/shared/components/ui/table';
import { cn } from '@mdvip/shared/lib/utils';
import type { DatasetState } from '@/hooks/member-views/useMemberDataset';
import type { ColumnWidth, MemberRowData, MemberTable } from '@/lib/member-views/memberDataset.types';
import { RetryButton, StateBoundary, type StateCopy, useStateCopy } from './StateBoundary';

export interface DirectoryTableProps<TRow extends MemberRowData> {
  table: MemberTable<TRow>;
  state: DatasetState;
  onRetry: () => void;
  /** Replaces the default "no patients" copy, e.g. when a letter or search matches nothing. */
  empty?: StateCopy;
}

const LOADING_ROWS = 6;

/**
 * The id of the row-actions column a view can append. Its header is for screen readers only, and
 * in the card layout its buttons sit along the bottom of the card rather than under a label.
 */
export const ACTIONS_COLUMN_ID = 'actions';

/**
 * Each column size as a width on its header. The table keeps its automatic layout, so these share
 * out the spare width in proportion and a value that can't fit still widens its column instead of
 * spilling into the next one.
 */
const COLUMN_WIDTH: Record<ColumnWidth, string> = {
  sm: 'w-28',
  md: 'w-36',
  lg: 'w-48',
  xl: 'w-64',
};

/**
 * Opaque, so cells scrolled sideways don't show through the pinned first column. The hover tint is
 * the row's `muted/50` mixed onto the card, so the pinned cell matches the rest of the row.
 */
const STICKY_CELL =
  'sticky left-0 z-10 bg-card transition-colors group-hover:bg-[color-mix(in_oklab,var(--muted)_50%,var(--card))]';

/**
 * DirectoryTable — a view's rows as a sortable table, folding to one card per patient below a
 * 768px container width. Both layouts are rendered; a container query shows one.
 *
 * Rows and headers are read inside `table.Subscribe`, so a sort or page change re-renders them even
 * where the React Compiler has memoized this component on an unchanged `table`.
 */
export function DirectoryTable<TRow extends MemberRowData>({
  table,
  state,
  onRetry,
  empty,
}: DirectoryTableProps<TRow>) {
  const copy = useStateCopy(empty);

  return (
    <div aria-busy={state === 'loading'} className="@container/md min-w-0">
      <table.Subscribe selector={(tableState) => ({ sorting: tableState.sorting, pagination: tableState.pagination })}>
        {() => {
          const rows = table.getRowModel().rows;
          const headerGroups = table.getHeaderGroups();
          const columnCount = table.getAllLeafColumns().length;
          // The view has patients, but none on screen: a letter or search that matches nobody.
          const shown: DatasetState = state === 'loaded' && rows.length === 0 ? 'empty' : state;

          return (
            <>
              <div className="hidden overflow-hidden rounded-2xl border bg-card @3xl/md:block">
                <Table>
                  <TableHeader sticky>
                    {headerGroups.map((group) => (
                      <TableRow key={group.id} className="hover:bg-transparent">
                        {group.headers.map((header, index) => {
                          const className = cn(
                            'whitespace-nowrap',
                            COLUMN_WIDTH[header.column.columnDef.meta?.width ?? 'md'],
                            index === 0 && 'sticky left-0 z-10 min-w-48 bg-card',
                          );
                          return header.column.getCanSort() ? (
                            <SortableTableHead
                              key={header.id}
                              direction={header.column.getIsSorted() || null}
                              onSort={() => header.column.toggleSorting()}
                              className={className}
                            >
                              <table.FlexRender header={header} />
                            </SortableTableHead>
                          ) : header.column.id === ACTIONS_COLUMN_ID ? (
                            <TableHead key={header.id} className={cn(className, 'text-right')}>
                              <span className="sr-only">
                                <table.FlexRender header={header} />
                              </span>
                            </TableHead>
                          ) : (
                            <TableHead key={header.id} className={className}>
                              <table.FlexRender header={header} />
                            </TableHead>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableHeader>
                  <TableBody>
                    {shown === 'loading' ? (
                      <TableLoading rows={LOADING_ROWS} columns={columnCount} label={copy.loadingLabel} />
                    ) : shown === 'error' ? (
                      <TableError
                        colSpan={columnCount}
                        title={copy.error.title}
                        description={copy.error.description}
                        action={<RetryButton onRetry={onRetry} />}
                      />
                    ) : shown === 'empty' ? (
                      <TableEmpty
                        colSpan={columnCount}
                        icon={<Icon name="users" size="md" className="text-muted-foreground" />}
                        title={copy.empty.title}
                        description={copy.empty.description}
                      />
                    ) : (
                      rows.map((row) => (
                        <TableRow key={row.id} className="group">
                          {row.getAllCells().map((cell, index) => (
                            <TableCell
                              key={cell.id}
                              className={cn(
                                'whitespace-nowrap text-sm',
                                index === 0 && STICKY_CELL,
                                cell.column.id === ACTIONS_COLUMN_ID && 'text-right',
                              )}
                            >
                              <table.FlexRender cell={cell} />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="@3xl/md:hidden">
                <StateBoundary state={shown} onRetry={onRetry} empty={empty}>
                  <ul className="flex flex-col gap-2">
                    {rows.map((row) => {
                      const cells = row.getAllCells();
                      const actions = cells.find((cell) => cell.column.id === ACTIONS_COLUMN_ID);
                      const [first, ...rest] = cells.filter((cell) => cell !== actions);
                      // Dataset columns carry their translated header as a string; a view's custom
                      // header component has no text form, so its column id stands in.
                      const labelOf = (cell: (typeof rest)[number]) => {
                        const { header } = cell.column.columnDef;
                        return typeof header === 'string' ? header : cell.column.id;
                      };
                      return (
                        <li key={row.id} className="flex flex-col gap-4 rounded-2xl border bg-card p-4">
                          {first && (
                            <div className="text-sm font-semibold text-foreground">
                              <table.FlexRender cell={first} />
                            </div>
                          )}
                          {rest.length > 0 && (
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-2">
                              {rest.map((cell) => (
                                <div key={cell.id} className="min-w-0">
                                  <dt className="text-xs text-muted-foreground">{labelOf(cell)}</dt>
                                  <dd className="mt-1 text-sm text-foreground">
                                    <table.FlexRender cell={cell} />
                                  </dd>
                                </div>
                              ))}
                            </dl>
                          )}
                          {actions && (
                            <div className="flex w-full items-center gap-2">
                              <table.FlexRender cell={actions} />
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </StateBoundary>
              </div>
            </>
          );
        }}
      </table.Subscribe>
    </div>
  );
}
